import { useMemo } from 'react';

import * as AllIllustrations from './index.js';
import type { IllustrationsList } from './index.js';

type SvgComponent = React.ComponentType<React.ComponentProps<'svg'>>;

function getModeSuffix(mode: string): 'Light' | 'Dark' {
  return mode === 'dark' ? 'Dark' : 'Light';
}

function hasThemedSuffix(name: string): boolean {
  return name.endsWith('Light') || name.endsWith('Dark');
}

function pickAvailableName(baseName: string, mode: string): string | null {
  if (hasThemedSuffix(baseName)) {
    return baseName in AllIllustrations ? baseName : null;
  }

  const themedCandidate = `${baseName}${getModeSuffix(mode)}`;
  if (themedCandidate in AllIllustrations) return themedCandidate;

  if (baseName in AllIllustrations) return baseName;

  return null;
}

export type UseIllustrationResult = {
  component: SvgComponent;
  name: IllustrationsList;
};

type UseIllustrationOptions = {
  mode?: 'light' | 'dark';
};

function resolveTwMode(options?: UseIllustrationOptions): 'light' | 'dark' {
  if (options?.mode) return options.mode;
  if (document !== undefined) {
    const root = document.documentElement;
    if (root.classList.contains('dark')) return 'dark';
    // also support data-theme convention
    if (root.dataset.colorScheme === 'dark') return 'dark';
  }
  // fallback to media query if available
  if (globalThis.window?.matchMedia) {
    return globalThis.window.matchMedia('(data-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return 'light';
}

export function useIllustration(
  name: string,
  options?: UseIllustrationOptions,
): UseIllustrationResult {
  const mode = resolveTwMode(options);

  const resolvedName = useMemo(
    () => pickAvailableName(name, mode),
    [name, mode],
  );

  const component = useMemo(() => {
    return AllIllustrations[
      resolvedName as keyof typeof AllIllustrations
    ] as unknown as SvgComponent;
  }, [resolvedName]);

  return {
    component,
    name: resolvedName as IllustrationsList,
  };
}

export default useIllustration;
